import Forms from './../../utils/forms/forms';
import { sortBatchOptions, defaultBatchId } from './../../utils/batchOptions';
import { mapGetters, mapMutations } from 'vuex';
import productService from './../../services/product';
import batchService from './../../services/batch';
import taxService from './../../services/tax';
import orderProductService from './../../services/orderProduct';
import unitTypeService from './../../services/unitType';
import ediOrderItemService from './../../services/ediOrderItem';
import productDepositService from './../../services/productDeposit';
import store from './../../store';
import QuickProductEdit from './../../components/quickProductEdit/quickProductEdit.vue';
import orderWarehousePopup from './../../components/orderWarehousePopup/orderWarehousePopup.vue';

export default {
  data() {
    return {
      form: new Forms({
        id: {
          value: '',
          type: 'text',
        },
        name: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        batchIds: {
          value: [],
          type: 'select',
        },
        transactions: {
          value: '',
          type: 'plaintext',
        },
        available: {
          value: '',
          type: 'plaintext',
        },
        price: {
          value: '',
          type: 'number',
        },
        productDepositName: {
          value: '',
          type: 'plaintext',
        },
        unitTypeId: {
          value: '',
          type: 'select',
        },
        quantityOrdered: {
          value: '',
          type: 'number',
        },
        quantityConfirmed: {
          value: '',
          type: 'number',
        },
        quantityReceived: {
          value: '',
          type: 'number',
        },
        taxId: {
          value: '',
          type: 'select',
        },
      }),
      hiddenFields: ['id'],
      ready: false,
    };
  },

  props: {
    /**
     * The current order.
     */
    order: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters({
      products: 'allProducts',
      orderProducts: 'allOrderProducts',
      currentEdiOrder: 'currentEdiOrder',
      taxes: 'allTaxes',
      batches: 'allBatches',
      productDeposits: 'allProductDeposits',
      account: 'account',
      currentOrder: 'currentOrder',
      unitTypes: 'allUnitTypes',
    }),

    checkHasInventory() {
      if (this.account.modules.length > 0) {
        for (let i = 0; i < this.account.modules.length; i++) {
          if (this.account.modules[i].name == 'Inventory') {
            return true;
          }
        }
      }

      return false;
    },

    hasProduction() {
      if (this.account.modules.length > 0) {
        return this.account.modules.find(module => module.name === 'Production');
      }
      if (this.account.allowProducerBatches) return true;
      return false;
    },

    list() {
      return this.orderProducts.map((p) => {
        let deposit = [];
        if (p.productDepositId) {
          deposit = this.productDeposits.all
            .filter(d => p.productDepositId.toString() === d.id.toString());
        }

        // dynamically add column
        if (p.returnCode != 0) {
          this.form.initialAssign({
            returnCodeDescription: {
              value: '',
              type: 'string',
            },
          });
        } else {
          this.form.removeField('returnCodeDescription');
        }

        if (!this.checkHasInventory) {
          this.form.removeField('available');
        }

        let value = '';
        if (deposit.length) {
          value = `${this.$t('static.orders.edit.deposit')}: ${deposit[0].name}`;
        }

        const nonBatchProduct = p.warehouses.filter(p => p.warehouseId === this.order.warehouseId.toString()).find(p => p.batchId === 0);
        let batchIds = [];
        if (p.batchIds.length) {
          batchIds = batchIds.concat(p.batchIds.map(id => id.toString()));
        } else if (nonBatchProduct) {
          batchIds = ['0'];
        }

        const transactions = p.warehouseTransactions.map((t) => {
          const batchObj = t.batch_id ? this.batches.find(b => +b.id === +t.batch_id) : '';
          const batch = batchObj ? batchObj.code : `${this.$t('static.orders.edit.nonBatch')}`;
          return `${batch} ( qty: ${t.adjustment_qty} )`;
        });

        const row = {
          batchIds,
          transactions: transactions ? transactions.join(', ') : '',
          productDepositName: value,
          quantityConfirmed: (this.is220 && p.quantityConfirmed === null) ? this.$t('static.orders.edit.unconfirmed') : p.quantityConfirmed,
        };
        if (p.taxPercentage !== undefined && p.taxPercentage !== null) {
          row.taxIdDisplay = p.taxPercentage;
        }
        return Object.assign({}, p, row);
      });
    },

    totalsTableList() {
      const fmt = val => Number(val).toLocaleString('nb-NO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const result = [{
        name: this.$t('static.orders.edit.subtotal'),
        value: fmt(this.subtotal),
      },
        {
          name: this.$t('static.orders.edit.tax'),
          value: fmt(this.tax),
        },
      ];

      if (this.discount > 0) {
        result.push({
          name: this.$t('static.orders.edit.discount'),
          value: fmt(this.discount),
        });
      }

      if (this.deposit > 0) {
        result.push({
          name: this.$t('static.orders.edit.deposit'),
          value: fmt(this.deposit),
        });
      }

      result.push({
        name: this.$t('static.orders.edit.total'),
        value: fmt(this.total),
      });

      return result;
    },

    /**
     * Whether table should appear or not.
     */
    showTable() {
      if (this.orderProducts.length > 0 || this.addingProduct || this.editingProduct) {
        return true;
      }

      return false;
    },

    subtotal() {
      return this.orderProducts.map(p => p.price * p.quantity).reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    discount() {
      return this.orderProducts.map(p => p.price * p.quantity * (p.discount / 100))
        .reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    tax() {
      return this.orderProducts.map(p => (
          p.price * p.quantity * (1 - (p.discount / 100)) * (p.taxPercentage / 100)
        )).reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    deposit() {
      return this.orderProducts.map(p => (
          p.productDeposit * p.quantity
        )).reduce((a, b) => a + b, 0)
        .toFixed(2);
    },

    total() {
      const total = Number(this.subtotal - this.discount);
      const totalTax = Number(this.deposit) + total + Number(this.tax);
      return totalTax.toFixed(2);
    },

    isZRE() {
      return this.currentEdiOrder.ediOrderTypeName === 'ZRE';
    },

    isZKA() {
      return this.currentEdiOrder.ediOrderTypeName === 'ZKA';
    },

    isZNB() {
      return this.currentEdiOrder.ediOrderTypeName === 'ZNB';
    },

    is220() {
      return this.currentEdiOrder.ediOrderTypeName === '220';
    },

    toBeConfirmed() {
      return this.is220 && this.currentEdiOrder.ediOrderStatusName === 'created';
    },

    isReceived() {
      return this.is220 && this.currentEdiOrder.ediOrderStatusName === 'received';
    },

    isCreated() {
      return this.is220 && this.currentEdiOrder.ediOrderStatusName === 'created';
    },

    isProcessed() {
      return this.is220 && this.currentEdiOrder.ediOrderStatusName === 'processed';
    },

    isConfirmed() {
      return this.is220 && this.currentEdiOrder.ediOrderStatusName === 'confirmed';
    },

    isCancelled() {
      return this.is220 && this.currentEdiOrder.ediOrderStatusName === 'cancelled';
    },

    canBeConfirmed() {
      if (['created', 'pending', 'out_of_stock'].includes(this.currentEdiOrder.ediOrderStatusName) && this.is220) {
        return true;
      }

      if (this.checkHasInventory && this.is220 && this.currentEdiOrder.ediOrderStatusName === 'received' && !this.hasOrderConfirmation) {
        return true;
      }

      return false;
    },
  },

  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalClass: 'SET_MODAL_CLASS',
    }),

    /**
     * Activates the modal
     */
    activateWarehouseModal() {
      this.setModalContent(() => orderWarehousePopup);
      this.setModalCloseOnClick(false);
      this.setModalShowClose(false);
      this.setModalCloseOnPressEscape(false);
      this.toggleModal(true);
    },

    /**
     * Search remote function
     */
    searchMethod(search, field) {
      return Promise.all([
        productService.search({ search, warehouse_id: '' }),
      ]);
    },
    /**
     * The handler for the quick product edit component that will be passed to the modal.
     *
     * @param  {Object} item The row item.
     * @return {Object}      The component.
     */
    onQuickProductEditMounted() {
      const that = this;

      // return function () {

      //    //
      //   // this.$once('close', () => {
      //   //   productService.all().then(() => {
      //   //     const filteredProducts = that.products
      //   //     .filter(p => p.id.toString() === this.orderProduct.productId.toString());
      //   //     if (filteredProducts.length) {
      //   //       that.form.assignData(this.orderProduct);
      //   //       that.form.assignData(filteredProducts[0]);
      //   //       that.update(this.orderProduct.id).then(() => {
      //   //         that.form.resetValues();
      //   //       });
      //   //     }
      //   //   });
      //   // });
      // };
    },

    updated(product, orderProduct) {
      this.form.assignData(orderProduct);
      this.form.assignData(product);
      return this.update(orderProduct.id, product);
      // console.log(productId, orderProduct);
      // orderProductService.all(this.order.id)
    },

    /**
     * Defines the handler for the links one the table.
     *
     * @param  {Object} item The row item.
     * @param  {String} name The field name.
     * @return {Mixed}      False or the handler function in case of a link needed.
     */
    tableClickHandler(item, name) {
      // Temporarilly disable due to quick edit errors.
      // if (!this.order.sent && item.productId && name === 'name' && this.canBeConfirmed) {
      //   return () => {
      //     this.setModalProps({
      //       productId: item.productId,
      //       orderProduct: item,
      //       isEdi: true,
      //       onMounted: this.onQuickProductEditMounted(),
      //       updated: this.updated,
      //     });
      //     this.toggleModal(true);
      //     this.setModalContent(() => QuickProductEdit);
      //   };
      // }

      return false;
    },

    /**
     * Returns a tag type in case of unconfirmed quantities.
     *
     * @param  {Object} item The row item.
     * @param  {String} name The field name.
     * @return {Mixed}      False or a tag type.
     */
    tableTagHandler(item, name) {
      if (this.is220 && name === 'quantityConfirmed' && isNaN(item.quantityConfirmed)) {
        return 'warning';
      }

      return false;
    },

    /**
     * Updates a order product.
     */
    update(orderProductId, product) {
      this.form.loading = true;

      const id = this.order.id;
      const data = this.form.data();

      delete data.productId;
      data.name = typeof data.name === 'object' ? data.name.name : data.name;
      data.productDepositId = product.productDepositId ?
        product.productDepositId : null;
      data.discount = 0;
      data.quantity = data.quantityOrdered;

      return new Promise((resolve, reject) => {
        orderProductService.update(id, data, orderProductId)
          .then(() => {
            orderProductService.all(this.order.id);
            this.form.loading = false;
            store.dispatch('updateOrderProduct', { orderProduct: data, id: orderProductId });
            resolve();
          }).catch((errors) => {
            this.form.loading = false;
            this.form.recordErrors(errors);
            const errorsKeys = Object.keys(this.form.errors.errors);
            this.$message({
              showClose: true,
              message: errors[errorsKeys[0]][0],
              customClass: 'error',
            });
            reject();
          });
      });
    },

    /**
     * This method is called when the user confirms the quantity of the item.
     */
    confirm(itemId) {
      const id = this.currentEdiOrder.id;
      const ediItemId = this.orderProducts
        .filter(p => itemId.toString() === p.id.toString())[0].ediOrderItemId;
      const data = this.form.data();

      if (data.quantityConfirmed === this.$t('static.orders.edit.unconfirmed')) {
        data.quantityConfirmed = 0;
      }

      if (data.quantityConfirmed > data.quantityOrdered) {
        return this.$message({
          showClose: true,
          message: this.$t('static.orders.edit.quantityMustNotBeGreaterThanQuantityOrdered'),
          type: 'error',
        });
      }

      return ediOrderItemService.confirm(id, data, ediItemId)
        .then(() => {
          orderProductService.all(this.order.id);
        }).catch((errors) => {
          this.form.recordErrors(errors);
          const errorsKeys = Object.keys(this.form.errors.errors);
          this.$message({
            showClose: true,
            message: errors[errorsKeys[0]][0],
            type: 'error',
          });
        });
    },

    /**
     * This method is called when the user wants to set the quantity confirmed value to quantity
     * ordered value.
     *
     * @param item
     */
    setMax() {
      this.form.quantityConfirmed = this.form.quantityOrdered;
    },

    /**
     * Delete the product from order.
     */
    destroy(orderProductId) {
      orderProductService.destroy(this.order.id, orderProductId);
    },

    getResources() {
      this.ready = false;
      const resources = [
        taxService.all(),
        orderProductService.all(this.order.id),
        productDepositService.all(),
        unitTypeService.all(),
      ];
      if (this.hasProduction) {
        resources.push(batchService.all({
          warehouse_id: this.order.warehouseId,
          has_warehouse_transaction: 1,
          for_select: 1,
        }));
      }
      Promise.all(resources)
        .then(() => {
          this.ready = true;
        })
        .catch(() => {
          this.ready = true;
        });
    },

    /**
     * This will be called when the form emit a change event.
     *
     * @param  {String} name    The input name.
     * @param  {String} value    The input value.
     */
    onFormChange(name, value) {
      this.form.clearSuccess(name, value);
      this.form.clearErrors(name);
      this.form[name] = value;
      if (name === 'quantityConfirmed' && value > this.form.quantityOrdered) {
        this.form[name] = this.form.quantityOrdered;
      }
      this.$forceUpdate();
    },

    configFieldVisibility() {
      if (!this.hasProduction) {
        this.hiddenFields = ['id', 'batchIds', 'transactions'];
      } else {
        this.hiddenFields = ['id'];
        if (this.toBeConfirmed || this.canBeConfirmed) {
          this.hiddenFields.push('transactions');
        } else {
          this.hiddenFields.push('available');
          this.hiddenFields.push('batchIds');
        }
      }
    },
  },

  watch: {
    /**
     * Fetch order products whenever order is ready.
     */
    order() {
      this.getResources();
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} taxes    The taxes list.
     */
    taxes(taxes) {
      this.form.setOptions('taxId', taxes.all.map(t => ({ id: t.id, name: t.taxPercentage })));
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} products    The products list.
     */
    productDeposits(deposits) {
      this.form.setOptions('productDepositId', deposits.all);
    },

    orderProducts(orderProducts) {
      this.configFieldVisibility();
    },

    unitTypes(unitTypes) {
      this.form.setOptions('unitTypeId', unitTypes.all.map(item => ({
        id: item.id.toString(),
        name: item.name,
      })));
    },

    'form.id': function (id) {
      if (id && this.hasProduction) {
        if (+this.$refs.formTable.$data.editingItem === +id) {
          if (this.checkHasInventory) {
            if (!this.currentOrder.warehouseId && this.currentOrder.orderStatus !== 'cancelled') {
              this.$refs.formTable.closeForm();
              this.activateWarehouseModal();
              return;
            }
          }
        }

        let orderProduct = this.list.find(item => item.id === id);

        if (!orderProduct && typeof this.form.name === 'object') {
          orderProduct = this.form.name;
        }

        if (orderProduct && orderProduct.warehouses) {
          let products = orderProduct.warehouses;
          if (products.length) {
            if (this.currentOrder.warehouseId) {
              products = products.filter(p => p.warehouseId === this.currentOrder.warehouseId.toString());
            }

            const batchIds = products.map(p => p.batchId.toString());

            let options = [];
            options = options.concat(this.batches.filter(b => (batchIds.indexOf(b.id) !== -1)).map((batch) => {
              const batchProduct = products.find(p => p.batchId.toString() === batch.id.toString());
              return Object.assign({}, {
                id: batch.id.toString(),
                name: this.currentOrder.warehouseId ? `${batch.code} | ${this.$t('static.orders.edit.availableQty')}: ${batchProduct.qtyNumber}` : batch.code,
                available: batchProduct.qtyNumber,
              });
            }));
            options = options.reverse();

            const nonBatchProduct = products.find(p => p.batchId === 0);
            if (nonBatchProduct) {
              options.unshift({
                id: '0',
                name: `${this.$t('static.orders.edit.nonBatch')} | ${this.$t('static.orders.edit.availableQty')}: ${nonBatchProduct.qtyNumber}`,
                available: nonBatchProduct.qtyNumber,
              });
            }

            // Stock is only known per warehouse; without one, keep the list as is.
            if (this.currentOrder.warehouseId) {
              options = sortBatchOptions(options, { hideEmpty: true, keepId: this.form.batchId });
            }

            this.form.setOverrideOptions('batchIds', options);
            if (!this.form.batchId && batchIds.length) {
              this.form.batchId = this.currentOrder.warehouseId ? defaultBatchId(options) : batchIds[0];
            }
          } else {
            this.form.batchId = '';
            this.form.setOverrideOptions('batchIds', []);
          }
        } else {
          this.form.batchId = '';
          this.form.setOverrideOptions('batchIds', []);
        }
      }
    },

    batches(batches) {
      const options = batches.map(batch => ({
        id: +batch.id,
        name: batch.code,
      }));
      this.form.setOptions('batchIds', [{
        id: 0,
        name: `${this.$t('static.orders.edit.nonBatch')}`,
      }, ...options]);
    },

    'form.batchIds': function (batchIds) {
      if (this.hasProduction) {
        let orderProduct = this.list.find(item => item.id === this.form.id);
        if (!orderProduct && typeof this.form.name === 'object') {
          orderProduct = this.form.name;
        }
        if (orderProduct) {
          let products = orderProduct.warehouses;
          if (this.order.warehouseId) {
            products = products.filter(p => p.warehouseId === this.order.warehouseId.toString());
          }
          const batchProducts = products.filter(p => batchIds.includes(p.batchId.toString()));
          let available = 0;
          if (batchProducts.length) {
            available = batchProducts.map(item => item.qtyNumber).reduce((total, qtyNumber) => total + qtyNumber);
          }
          this.form.available = available.toString();
        }
      }
    },

  },

  mounted() {
    this.getResources();
    Object.keys(this.form.fields).forEach((field) => {
      if (field !== 'batchIds' && field !== 'quantityConfirmed') {
        this.form.disableField(field);
      }
    });
    this.configFieldVisibility();
    this.form.setExtraAttr('batchIds', {
      multiple: true,
    });
  },

  components: {
    VFormTable: require('./../formTable/formTable.vue'),
    VTotalsTable: require('./../totalsTable/totalsTable.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/ediOrderItemsTable/ediOrderItemsTable.js